import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TaskDetailResponse, TaskListItem } from "../../../api/requests";
import { Shimmer } from "../../../components/Shimmer/Shimmer";
import { formatDate, getStatusLabel } from "../requestDetailUtils";

export interface RequestDetailTaskDeckProps {
  taskListLoading: boolean;
  taskList: TaskListItem[] | null;
  selectedTaskIndex: number;
  taskListCurrentPage: number;
  taskListTotalPages: number;
  taskDetail: TaskDetailResponse | null;
  taskDetailLoading: boolean;
  taskDetailsCache: Record<string, TaskDetailResponse>;
  deckViewportWidth: number;
  deckCardsRef: React.RefObject<HTMLDivElement>;
  pdfContentRef: React.RefObject<HTMLDivElement>;
  handlePrevTask: () => void;
  handleNextTask: () => void | Promise<void>;
  handleDownloadPdf: () => void | Promise<void>;
}

export const RequestDetailTaskDeck: React.FC<RequestDetailTaskDeckProps> = ({
  taskListLoading,
  taskList,
  selectedTaskIndex,
  taskListCurrentPage,
  taskListTotalPages,
  taskDetail,
  taskDetailLoading,
  taskDetailsCache,
  deckViewportWidth,
  deckCardsRef,
  pdfContentRef,
  handlePrevTask,
  handleNextTask,
  handleDownloadPdf,
}) => {
  if (!taskListLoading && !taskList) return null;

  return (
    <div className="detail-deck">
      {taskListLoading ? (
        <div className="detail-card">
          <Shimmer rows={3} />
        </div>
      ) : taskList && taskList.length > 0 ? (
        <>
          <div className="detail-deck-counter detail-deck-counter-top">
            {selectedTaskIndex + 1}
          </div>
          <div className="detail-deck-content">
            {selectedTaskIndex > 0 && (
              <button
                type="button"
                className="detail-deck-arrow detail-deck-arrow-hide-mobile"
                onClick={handlePrevTask}
                aria-label="Предыдущая"
              >
                <ChevronLeft size={28} />
              </button>
            )}
            <div className="detail-deck-cards" ref={deckCardsRef}>
              <div
                className="detail-deck-track"
                style={
                  deckViewportWidth > 0
                    ? {
                        width: taskList.length * deckViewportWidth,
                        transform: `translateX(${-selectedTaskIndex * deckViewportWidth}px)`,
                      }
                    : {
                        width: `${(taskList?.length || 1) * 100}%`,
                        transform: `translateX(${-((selectedTaskIndex / (taskList?.length || 1)) * 100)}%)`,
                      }
                }
              >
                {taskList.map((task, idx) => {
                  const detail =
                    taskDetailsCache[task.id] ??
                    (idx === selectedTaskIndex ? taskDetail : null);
                  const isLoading = idx === selectedTaskIndex && taskDetailLoading;
                  const isTaskError =
                    detail?.status === "error" || task.status === "error";
                  const canDownloadPdf =
                    !isTaskError &&
                    task.status !== "new" &&
                    task.status !== "in_progress" &&
                    task.status !== "processing";
                  return (
                    <div
                      key={task.id}
                      className="detail-deck-card-visible detail-card"
                      style={
                        deckViewportWidth > 0
                          ? {
                              width: deckViewportWidth,
                              flex: `0 0 ${deckViewportWidth}px`,
                            }
                          : {
                              flex: `0 0 ${100 / taskList.length}%`,
                            }
                      }
                    >
                      {isLoading ? null : detail ? (
                        <>
                          {canDownloadPdf && (
                            <button
                              type="button"
                              className="detail-schedule-btn"
                              onClick={handleDownloadPdf}
                            >
                              Скачать PDF
                            </button>
                          )}
                          <div ref={idx === selectedTaskIndex ? pdfContentRef : null}>
                            <div className="detail-dates-row">
                              <div className="detail-row">
                                <span className="detail-label">Начало</span>
                                <span className="detail-value">
                                  {formatDate(detail.startDateTime)}
                                </span>
                              </div>
                              {detail.endDateTime && (
                                <div className="detail-row">
                                  <span className="detail-label">Окончание</span>
                                  <span className="detail-value">
                                    {formatDate(detail.endDateTime)}
                                  </span>
                                </div>
                              )}
                            </div>
                            {task.status === "new" || task.status === "in_progress" ? (
                              <div className="detail-report-placeholder">
                                <div className="detail-report-progress">
                                  <div className="detail-report-progress-bar" />
                                </div>
                                После выполнения проверки, здесь будут отображены результаты
                              </div>
                            ) : isTaskError ? (
                              <div className="detail-report-placeholder detail-report-error">
                                При проверке произошла ошибка, повторите позднее
                              </div>
                            ) : (
                              detail.tasks &&
                              detail.tasks.length > 0 && (
                                <div className="detail-report-tasks">
                                  <table className="detail-tasks-table">
                                    <thead>
                                      <tr>
                                        <th>Проверка</th>
                                        <th>Результат</th>
                                        <th>Детали</th>
                                        <th>Рекомендация</th>
                                        <th>Ответственность</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.tasks.map((t, index) => (
                                        <tr
                                          key={`${t.checkName}-${t.taskId}-${index}`}
                                          className={
                                            t.color ? `detail-task-row--${t.color}` : undefined
                                          }
                                        >
                                          <td>{t.checkNameDescription ?? t.checkName}</td>
                                          <td>{t.resultDescription ?? t.result}</td>
                                          <td>{t.details?.trim() || "—"}</td>
                                          <td>{t.suggestion?.trim() || "—"}</td>
                                          <td>{t.responsibility?.trim() || "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )
                            )}
                          </div>
                        </>
                      ) : !isLoading ? (
                        <>
                          <div className="detail-row">
                            <span className="detail-label">Статус</span>
                            <span className="detail-value">{getStatusLabel(task.status)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Начало</span>
                            <span className="detail-value">{formatDate(task.startDateTime)}</span>
                          </div>
                          {task.endDateTime && (
                            <div className="detail-row">
                              <span className="detail-label">Окончание</span>
                              <span className="detail-value">{formatDate(task.endDateTime)}</span>
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            {(selectedTaskIndex < taskList.length - 1 ||
              taskListCurrentPage < taskListTotalPages) && (
              <button
                type="button"
                className="detail-deck-arrow detail-deck-arrow-hide-mobile"
                onClick={handleNextTask}
                aria-label="Следующая"
              >
                <ChevronRight size={28} />
              </button>
            )}
            <div className="detail-deck-nav">
              {selectedTaskIndex > 0 ? (
                <button
                  type="button"
                  className="detail-deck-nav-arrow"
                  onClick={handlePrevTask}
                  aria-label="Предыдущая"
                >
                  <ChevronLeft size={24} />
                </button>
              ) : (
                <span style={{ width: 48, minWidth: 48 }} />
              )}
              <span className="detail-deck-counter">{selectedTaskIndex + 1}</span>
              {selectedTaskIndex < taskList.length - 1 ||
              taskListCurrentPage < taskListTotalPages ? (
                <button
                  type="button"
                  className="detail-deck-nav-arrow"
                  onClick={handleNextTask}
                  aria-label="Следующая"
                >
                  <ChevronRight size={24} />
                </button>
              ) : (
                <span style={{ width: 48, minWidth: 48 }} />
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="detail-card">
          <span className="detail-value">Нет задач</span>
        </div>
      )}
    </div>
  );
};
